import React, { memo, useMemo } from 'react';

import { Paper, Popper, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';

import {
  getDropdownItemActiveStyles,
  getDropdownItemStyles,
} from './addressSuggestionDropdown.styles';

const renderDefaultContent = suggestion => {
  const { street, unit, city, state, postcode } = suggestion.raw;
  const primary = [street, unit].filter(Boolean).join(' ');
  const secondary = [city, state, postcode].filter(Boolean).join(', ');

  return (
    <>
      {primary && (
        <Typography component="span" variant="medium14">
          {primary}
        </Typography>
      )}
      {primary && secondary && ' — '}
      {secondary && (
        <Typography color="text.secondary" component="span" variant="regular12">
          {secondary}
        </Typography>
      )}
    </>
  );
};

const AddressSuggestionDropdown = ({
  anchorEl,
  suggestions,
  activeIndex,
  onItemClick,
  onItemMouseDown,
  onItemMouseEnter,
  onItemMouseLeave,
  renderItemContent,
}) => {
  const theme = useTheme();
  const popperStyle = useMemo(
    () => ({ zIndex: 1300, width: anchorEl?.offsetWidth }),
    [anchorEl?.offsetWidth],
  );
  const itemStyle = useMemo(() => getDropdownItemStyles(theme), [theme]);
  const activeItemStyle = useMemo(() => getDropdownItemActiveStyles(theme), [theme]);

  if (!suggestions.length || !anchorEl) {
    return null;
  }

  return (
    <Popper open anchorEl={anchorEl} placement="bottom-start" style={popperStyle}>
      <Paper elevation={3} role="listbox">
        {suggestions.map((suggestion, index) => {
          const isActive = index === activeIndex;
          const { street, city, state, postcode } = suggestion.raw;
          const itemKey = `${street}-${city}-${state}-${postcode}-${index}`;

          return (
            <div
              aria-selected={isActive}
              data-index={index}
              data-item-id={index}
              key={itemKey}
              role="option"
              style={isActive ? activeItemStyle : itemStyle}
              onClick={onItemClick}
              onMouseDown={onItemMouseDown}
              onMouseEnter={onItemMouseEnter}
              onMouseLeave={onItemMouseLeave}
            >
              {renderItemContent ? renderItemContent(suggestion) : renderDefaultContent(suggestion)}
            </div>
          );
        })}
      </Paper>
    </Popper>
  );
};

AddressSuggestionDropdown.propTypes = {
  activeIndex: PropTypes.number.isRequired,
  suggestions: PropTypes.array.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onItemMouseDown: PropTypes.func.isRequired,
  onItemMouseEnter: PropTypes.func.isRequired,
  onItemMouseLeave: PropTypes.func.isRequired,
  anchorEl: PropTypes.object,
  renderItemContent: PropTypes.func,
};

export default memo(AddressSuggestionDropdown);
